/**
 * Keyword variant pages for the thumbnail downloader (HD, Shorts). Each reuses
 * the same grabber component with its own copy and optional highlighted
 * resolution. One dynamic `$variant` route renders any entry.
 */
import { m } from "@/paraglide/messages";

export interface YtFaq {
  q: () => string;
  a: () => string;
}

export interface YtVariant {
  slug: string;
  path: string;
  navName: () => string;
  metaTitle: () => string;
  metaDescription: () => string;
  h1: () => string;
  intro: () => string;
  howTo: () => string;
  faqs: YtFaq[];
  /** Thumbnail quality key to outline as the recommended one. */
  highlight?: string;
}

const BASE = "/youtube-thumbnail-downloader";

export const YT_VARIANTS: Record<string, YtVariant> = {
  hd: {
    slug: "hd",
    path: `${BASE}/hd`,
    highlight: "maxresdefault",
    navName: () => m.yt_hd_nav(),
    metaTitle: () => m.yt_hd_meta_title(),
    metaDescription: () => m.yt_hd_meta_description(),
    h1: () => m.yt_hd_h1(),
    intro: () => m.yt_hd_intro(),
    howTo: () => m.yt_hd_howto(),
    faqs: [
      { q: () => m.yt_hd_faq1_q(), a: () => m.yt_hd_faq1_a() },
      { q: () => m.yt_hd_faq2_q(), a: () => m.yt_hd_faq2_a() },
      { q: () => m.yt_hd_faq3_q(), a: () => m.yt_hd_faq3_a() },
      { q: () => m.yt_hd_faq4_q(), a: () => m.yt_hd_faq4_a() },
    ],
  },
  shorts: {
    slug: "shorts",
    path: `${BASE}/shorts`,
    navName: () => m.yt_shorts_nav(),
    metaTitle: () => m.yt_shorts_meta_title(),
    metaDescription: () => m.yt_shorts_meta_description(),
    h1: () => m.yt_shorts_h1(),
    intro: () => m.yt_shorts_intro(),
    howTo: () => m.yt_shorts_howto(),
    faqs: [
      { q: () => m.yt_shorts_faq1_q(), a: () => m.yt_shorts_faq1_a() },
      { q: () => m.yt_shorts_faq2_q(), a: () => m.yt_shorts_faq2_a() },
      { q: () => m.yt_shorts_faq3_q(), a: () => m.yt_shorts_faq3_a() },
      { q: () => m.yt_shorts_faq4_q(), a: () => m.yt_shorts_faq4_a() },
    ],
  },
};

export const YT_VARIANT_LIST: YtVariant[] = Object.values(YT_VARIANTS);

/**
 * Safe lookup by slug (`Object.hasOwn` so "constructor"/"__proto__"/… resolve to
 * undefined instead of an inherited member, which would crash the page).
 */
export function getVariant(slug: string): YtVariant | undefined {
  return Object.hasOwn(YT_VARIANTS, slug) ? YT_VARIANTS[slug] : undefined;
}
